import React from "react";
import { Globe, Zap, Sparkles, TrendingUp, Trophy, ImageIcon, ShieldCheck, Gauge } from "lucide-react";
import { METRICS } from "../data/metrics";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";
import { useSeo, breadcrumbLd, softwareLd } from "../lib/seo";

const BLOCKS = [
  { icon: Zap, title: "8-metric facial analysis", body: "Every scan measures symmetry, jawline, facial harmony, golden ratio, facial thirds, canthal tilt, cheekbones and skin quality on a 100-point scale — with a note explaining each one." },
  { icon: Sparkles, title: "AI Coach with a real plan", body: "Instead of a single number, you get a structured multi-week improvement plan tailored to your face's biggest opportunities, with concrete steps and timeframes." },
  { icon: TrendingUp, title: "Future-Face projection", body: "We hold your bone structure constant and project your realistic ceiling from optimising skin, body fat, grooming and posture — a target you can actually hit." },
  { icon: Globe, title: "Works on any device", body: "No App Store download and no iPhone TrueDepth sensor. Aesthetiq runs in any browser on iOS, Android, desktop and tablet." },
  { icon: Trophy, title: "Leaderboard & Face Battles", body: "Make your FaceCard public to climb the global ranking and challenge others head-to-head." },
  { icon: ImageIcon, title: "Viral share pages & OG cards", body: "Every result gets a permanent, indexable page with an auto-generated branded image so it unfurls beautifully on social." },
];

export default function Features() {
  useSeo({
    title: "Features — AI facial analysis, coaching, progress & sharing",
    description: "Explore Aesthetiq's features: 8-metric facial analysis, AI Coach improvement plans, Future-Face projection, progress tracking, leaderboard and viral share pages.",
    path: "/features",
    jsonLd: [softwareLd, breadcrumbLd([{ name: "Home", path: "/" }, { name: "Features", path: "/features" }])],
  });

  return (
    <div>
      <section className="container py-16 text-center">
        <Reveal>
          <span className="label-eyebrow">Features</span>
          <h1 className="mx-auto mt-3 max-w-3xl font-display text-5xl text-balance sm:text-6xl">A complete facial-aesthetics studio in your browser.</h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Everything you need to measure, understand and improve your facial presence — no download required.</p>
        </Reveal>
      </section>

      <section className="container pb-10">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {BLOCKS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.05}>
              <div className="h-full rounded-xl border border-border bg-card p-7 transition hover:border-primary/40">
                <b.icon className="h-7 w-7 text-primary" />
                <h2 className="mt-4 font-display text-xl">{b.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-card/40">
        <div className="container py-16">
          <Reveal>
            <span className="label-eyebrow">The 8 metrics</span>
            <h2 className="mt-3 font-display text-4xl">What we measure, and why.</h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {METRICS.map((m, i) => (
              <Reveal key={m.key} delay={i * 0.03}>
                <div className="flex gap-4 rounded-lg border border-border bg-card p-5">
                  <Gauge className="h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <h3 className="font-display text-lg">{m.label}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{m.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container py-16">
        <Reveal>
          <div className="flex items-start gap-4 rounded-xl border border-border bg-card p-7">
            <ShieldCheck className="h-7 w-7 shrink-0 text-success" />
            <div>
              <h2 className="font-display text-2xl">Private by design</h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">You decide whether each FaceCard is public or private. Private scans never appear on the leaderboard or in shares, and you can delete any scan from your dashboard at any time.</p>
            </div>
          </div>
        </Reveal>
      </section>

      <CtaSection />
    </div>
  );
}
